import React, { useEffect } from 'react'
import { Form, Input, Row, Col, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { Modal } from '~/Components/Modal'
import { OrganizationsService } from '~/Services/OrganizationService'

interface AddCompanyModalProps {
  visible: boolean
  onClose: () => void
  onSuccess?: () => void
}

export const AddCompanyModal: React.FC<AddCompanyModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [loading, setLoading] = React.useState(false)

  useEffect(() => {
    if (visible) {
      form.resetFields()
    }
  }, [visible, form])

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true)

      await OrganizationsService.Post.onboardOrganization({
        organization: {
          companyName: values.companyName,
          code: values.code || '',
          taxCode: values.taxCode || '',
          note: values.note || '',
          billingAddress: values.billingAddress || '',
          billingAddress2: values.billingAddress2 || '',
          billingCity: values.billingCity || '',
          billingState: values.billingState || '',
          billingZip: values.billingZip || '',
          crmId: '',
          duns: values.duns || '',
          naics: values.naics || '',
          phoneNumber: values.phoneNumber || '',
        },
        adminEmail: values.adminEmail,
      })

      message.success(t('admin.createCompanySuccess'))
      form.resetFields()
      onClose()

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || t('admin.createCompanyFailed'),
      )
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title={t('admin.addNewCompany')}
      visible={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={700}
      okText={t('admin.createCompany')}
      cancelText={t('admin.cancel')}
      loadingData={loading}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('admin.companyName')}
              name="companyName"
              rules={[
                { required: true, message: t('admin.pleaseEnterCompanyName') },
              ]}
            >
              <Input placeholder={t('admin.enterCompanyName')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={t('admin.code')}
              name="code"
              rules={[{ max: 50, message: t('admin.codeMaxLength') }]}
            >
              <Input placeholder={t('admin.enterCode')} maxLength={50} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('admin.adminEmail')}
              name="adminEmail"
              rules={[
                { required: true, message: t('admin.pleaseEnterAdminEmail') },
                { type: 'email', message: t('admin.invalidAdminEmail') },
              ]}
            >
              <Input placeholder={t('admin.enterAdminEmail')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('admin.taxCode')}
              name="taxCode"
              rules={[{ max: 50, message: t('admin.taxCodeMaxLength') }]}
            >
              <Input placeholder={t('admin.enterTaxCode')} maxLength={50} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item 
              label={t('admin.duns')} 
              name="duns"
            >
              <Input placeholder={t('admin.enterDuns')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={t('admin.phone')} name="phoneNumber">
              <Input placeholder={t('admin.enterPhone')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={t('admin.naics')} name="naics">
              <Input placeholder={t('admin.enterNaics')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('admin.homeAddress')}
              name="billingAddress"
            >
              <Input placeholder={t('admin.enterHomeAddress')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={t('admin.companyAddress')} name="billingAddress2">
              <Input placeholder={t('admin.enterCompanyAddress')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label={t('admin.city')}
              name="billingCity"
            >
              <Input placeholder={t('admin.enterCity')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('admin.state')} name="billingState">
              <Input placeholder={t('admin.enterState')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('admin.zipCode')} name="billingZip">
              <Input placeholder={t('admin.enterZipCode')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={t('admin.note')} name="note">
          <Input.TextArea placeholder={t('admin.enterNote')} rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  )
}